import { DataGrid, EmptyState, Icon, theme, Spinner, TableCell } from '@ketch-com/deck'
import { Box, Typography } from '@mui/material'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { useDataSentryEventsList } from 'api/dataSentry/queries/useDataSentryEventsList'
import { useTableFilters } from 'pages/dataSentry/hooks/useTableFilters'
import { DataSentryTableFilters } from 'pages/dataSentry/components/DataSentryTableFilters'
import { useMemo, useRef } from 'react'
import { Classification, ClassificationType, EventListItem } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import {
  CalssificationExpansionDropdown,
  ClassificationIdString,
} from 'pages/dataSystems/DataSystem/assetsTab/components/ClassificationExpansionDropdown'
import { sortAlphabetically } from 'pages/dataSystems/DataSystem/assetsTab/utils/sortAlphabetically'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { enumToLabelChipVariantMap } from 'pages/dataSentry/constants'
import pluralize from 'pluralize'

export const CollectionDetails: React.FC = () => {
  const { selectedTimeRange, selectedPropertyCode, hasPropertyAndScanTimeRange, systemDetails } = useDataSentryContext()
  const { searchQuery, debouncedSearchQuery, setSearchQuery, paginationModel, setPaginationModel } = useTableFilters(10)

  const { data: eventsList, isLoading: isLoadingEventsList } = useDataSentryEventsList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'data-system-code': systemDetails?.dataSystem?.code,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const rowCountRef = useRef(eventsList?.total || 0)
  const rowCount = useMemo(() => {
    if (eventsList?.total !== undefined) {
      rowCountRef.current = eventsList?.total
    }
    return rowCountRef.current
  }, [eventsList?.total])

  const columns: GridColDef<EventListItem>[] = [
    {
      align: 'left',
      field: 'collectorUrl',
      headerName: 'Collector URL',
      minWidth: 540,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventListItem>) => {
        return (
          <TableCell>
            <Typography variant="label">{row.url}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'right',
      field: 'collectionEvents',
      headerName: 'Collection Events',
      minWidth: 70,
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          variant="smallLabel"
          sx={{ textWrap: 'wrap' }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventListItem>) => {
        return (
          <TableCell>
            <Typography variant="body">{row.count}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'datacategories',
      headerName: pluralize('Data Category', 2),
      headerAlign: 'left',
      minWidth: 450,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventListItem>) => {
        let displayItems
        let dropDownListItems: Classification[] = []
        const sortedDataCategoriesList = sortAlphabetically(
          row.classificationsByType?.find(classificaionsLists => {
            return classificaionsLists.classificationType === ClassificationType.DATA_CATEGORY
          })?.classifications || [],
          'name',
        )
        if (sortedDataCategoriesList.length <= 2) {
          displayItems = sortedDataCategoriesList
        } else {
          displayItems = sortedDataCategoriesList.slice(0, 2) as Classification[]
          dropDownListItems = sortedDataCategoriesList.slice(2) as Classification[]
        }

        return (
          <TableCell>
            {displayItems && displayItems.length ? (
              <>
                {displayItems.map((classification: Classification) => (
                  <AssetsTabTableLabelChip
                    key={classification.code}
                    title={classification.name || classification.code}
                    variant={enumToLabelChipVariantMap[ClassificationType.DATA_CATEGORY]}
                    hideConfidence
                    hideIcon
                    isViewOnly
                  />
                ))}
                {dropDownListItems.length > 0 && (
                  <CalssificationExpansionDropdown
                    label={`+${dropDownListItems.length} more`}
                    items={dropDownListItems}
                    labelChipVariant={enumToLabelChipVariantMap[ClassificationType.DATA_CATEGORY]}
                    idKeyValue={ClassificationIdString.assetDataCategoryId}
                    isViewOnly
                    hideIcon
                    handleManualClassificationVerification={() => {}}
                  />
                )}
              </>
            ) : (
              <EmptyValueRenderer />
            )}
          </TableCell>
        )
      },
    },
  ]

  const displayDataGridEmptyState = !isLoadingEventsList && !eventsList?.events?.length && !debouncedSearchQuery
  const displayDataGrid =
    !isLoadingEventsList && (eventsList?.events?.length || (!eventsList?.events?.length && debouncedSearchQuery))

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <DataSentryTableFilters searchQuery={searchQuery} handleSearchInputChange={setSearchQuery} />
      {isLoadingEventsList ? (
        <Box display="flex" width="100%" alignItems="center" justifyContent="center" height={350}>
          <Spinner size={32} thickness={2.5} />
        </Box>
      ) : null}

      {displayDataGridEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px">
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={<Icon name="OTableView" width="48px" height="48px" iconColor={theme.palette.Black.o48} />}
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {displayDataGrid ? (
        <Box height="60vh">
          <DataGrid
            sx={{
              '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
                backgroundImage: 'unset',
                cursor: 'auto',
              },
              ...(isLoadingEventsList
                ? {
                    '& .MuiDataGrid-virtualScroller': {
                      height: '300px',
                    },
                  }
                : {}),
            }}
            autosizeOnMount
            autosizeOptions={{
              expand: true,
              includeHeaders: true,
              includeOutliers: false,
            }}
            getRowHeight={() => 'auto'}
            columns={columns}
            getRowId={row => row.url}
            rows={eventsList?.events || []}
            checkboxSelection={false}
            hideFooter={false}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnResize
            disableRowHoverStates
            disableChildrenSorting={false}
            loading={isLoadingEventsList}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20]}
            noRowsOverlayProps={{ buttonTitle: '' }}
          />
        </Box>
      ) : null}
    </Box>
  )
}
